import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Radom, { SearchResult } from '../state/Radom'
import { RADOM_COLORS } from '../util/Constants'
import Spinner from './Spinner'
import Close from '../icons/Close'
import { IconButton } from './Button'
import { Link } from 'react-router-dom'
import { getMethod } from '../util/Managed'

const SearchContainer = styled.div`
  width: 100%;
  flex-shrink: 1; 
  position: relative;
`

const SearchInput = styled.input<{ $focused: boolean }>`
  background-color: var(--surface, #ffffff);
  border: 1px solid var(--border-strong, #d8dde7);
  border-radius: 16px;
  display: flex;
  width: 100%;
  transition: 0.18s ease all;
  min-height: 44px;
  padding: 0 16px;
  outline: 0;
  color: var(--ink-strong, #313745);
  padding-right: 38px;
  font-size: 14.5px;
  box-sizing: border-box;
  
  &::placeholder {
    color: var(--text-faint, #9299a8);
  }
  
  ${({ $focused }) => $focused
    ? `
    color: black;
    border: 1px solid var(--focus-border, rgba(17, 17, 17, 0.22));
    box-shadow: 0 0 0 4px var(--focus-ring, rgba(17, 17, 17, 0.06));
    background-color: var(--surface, #ffffff);
    flex-grow: 1;

    &::placeholder {
      color: var(--text-faint, #9299a8);
    }`
    : `  
    &:hover {
      background-color: var(--surface, #ffffff);
      border: 1px solid var(--border-strong, #bfc6d2);
    }`
}
`

const SearchResultPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const SearchResultItem = styled(Link)`
  width: 100%;
  overflow: auto;
  padding: 16px 18px;
  border-bottom: 1px solid ${RADOM_COLORS.GRAY_DARK};
  font-size: 14px;
  cursor: pointer;
  transition: 0.2s ease all;
  user-select: none;
  display: block;
  box-sizing: border-box;
  text-decoration: none;
  color: black;

  &:hover {
    background-color: ${RADOM_COLORS.GRAY_LIGHTEST};
  }

  &:active {
    opacity: 0.5;
  }
`

const SearchBox = observer((): ReactElement => {
  const [focused, setFocused] = useState(false)
  const [searchText, setSearchText] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [searchResults, setSearchResults] = useState([] as SearchResult[])

  const searchContainer = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const query = searchText.trim()
    if (!query) {
      setSearchResults([])
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    Radom.search(query)
      .then(res => {
        if (!cancelled) {
          setSearchResults(res)
        }
      }).catch(err => {
        console.error(err)
      }).finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [searchText])

  const onMouseClick = (e: MouseEvent): void => {
    if (!(e.target as any === searchContainer || searchContainer.current?.contains(e.target as any))) {
      setFocused(false)
    }
  }

  const searchResultLink = (s: SearchResult): string => {
    const withTransactionsSource = (path: string): string => path.includes('?')
      ? `${path}&source=transactions`
      : `${path}?source=transactions`

    const paymentEventTypes = ['payment', 'openBanking', 'incompletePayment', 'recurringPayment', 'subscriptionPayment']
    if (s.managedEvent && paymentEventTypes.includes(s.managedEvent.eventType)) {
      return withTransactionsSource(`/payment/${s.managedEvent.id}`)
    }

    if (s.managedEvent && s.managedEvent.eventType === 'withdrawal') {
      return withTransactionsSource(`/withdrawal/${s.managedEvent.eventData.withdrawal.withdrawalRequestId}`)
    }

    if (s.managedEvent && s.managedEvent.eventType === 'deposit') {
      return withTransactionsSource(`/deposit/${s.managedEvent.eventData.deposit.depositId}`)
    }

    if (s.managedEvent && s.managedEvent.eventType === 'refund') {
      return '/transactions'
    }

    const subscriptionEvent =
      s.managedEvent?.eventData.newSubscription ||
      s.managedEvent?.eventData.subscriptionCancelled ||
      s.managedEvent?.eventData.subscriptionExpired
    if (subscriptionEvent) {
      return withTransactionsSource(`/subscription/${subscriptionEvent.subscriptionId}`)
    }

    if (s.checkoutSession) {
      return withTransactionsSource(`/checkout_session/${s.checkoutSession.id}`)
    }

    return '/transactions'
  }

  const getPaymentLabel = (eventType: string): string => {
    switch (eventType) {
    case 'openBanking':
      return 'Open banking payment'
    case 'subscriptionPayment':
      return 'Subscription payment'
    case 'recurringPayment':
      return 'Recurring payment'
    case 'incompletePayment':
      return 'Incomplete payment'
    default:
      return 'Payment'
    }
  }

  useEffect(() => {
    window.addEventListener('click', onMouseClick)
    return () => window.removeEventListener('click', onMouseClick)
  }, [])

  return <SearchContainer ref={searchContainer}>
    <SearchInput
      onChange={e => setSearchText(e.target.value)}
      value={searchText}
      onFocus={() => setFocused(true)}
      $focused={focused}
      placeholder='Search products, payments, etc.'
      autoCorrect='off'
      spellCheck='false' />

    {
      searchText.length > 0 && focused &&
      <IconButton
        onMouseDown={e => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation()
          setSearchText('')
        }}
        style={{
          position: 'absolute',
          right: 14,
          top: 12,
          padding: 0
        }}>
        <Close style={{ width: 12, height: 'auto' }} strokeWidth={1.5} />
      </IconButton>
    }

    <div style={{
      display: focused ? 'block' : 'none',
      position: 'absolute',
      marginTop: 8,
      minWidth: 'min(400px, calc(100vw - 32px))',
      width: '100%',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '80vh',
      overflow: 'auto',
      backgroundColor: 'var(--surface, #ffffff)',
      zIndex: 5,
      border: '1px solid var(--border, #e3e7ef)',
      borderRadius: 16,
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.1)'
    }}>
      {
        !isLoading &&
        searchResults
          // .filter(r => r.managedEvent?.eventData.payment || r.managedEvent?.eventData.newSubscription)
          .map((res, i) => {
            if (res.checkoutSession) {
              return <SearchResultItem key={`${res.checkoutSession.id}-checkoutSession`} to={searchResultLink(res)} onClick={() => setFocused(false)}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>Checkout Session for {res.checkoutSession.total} {res.checkoutSession.currency || 'USD'}</span>
                    </div>
                    <span style={{ opacity: 0.4 }}>{res.checkoutSession.id}</span>
                  </div>
                  <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Checkout Session</span>
                </div>
              </SearchResultItem>
            }

            if (!res.managedEvent) {
              return null
            }

            const payment = res.managedEvent.eventData.payment ||
             res.managedEvent.eventData.openBanking ||
             res.managedEvent.eventData.incompletePayment ||
             res.managedEvent.eventData.recurringPayment ||
             res.managedEvent.eventData.subscriptionPayment
            if (payment) {
              const { paymentMethod } = payment
              const method = getMethod(paymentMethod.network, paymentMethod.token)
              const label = getPaymentLabel(res.managedEvent.eventType)

              return <SearchResultItem key={i} to={searchResultLink(res)} onClick={() => setFocused(false)}>
                {
                  payment &&
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url(${method.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                                              </div>
                      <span>{label} for {Number(payment.amount)}</span>
                    </div>

                    <span style={{ opacity: 0.4 }}>{res.managedEvent?.id}</span>
                  </div>

                  <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>{label}</span>
                </div>
                }
              </SearchResultItem>
            }

            const withdrawal = res.managedEvent.eventData.withdrawal
            if (withdrawal) {
              const method = getMethod(withdrawal.network, withdrawal.token)

              return <SearchResultItem key={i} to={searchResultLink(res)} onClick={() => setFocused(false)}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url(${method.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }}>
                                              </div>
                      <span>Withdrawal for {Number(withdrawal.withdrawalAmount)}</span>
                    </div>

                    <span style={{ opacity: 0.4 }}>{withdrawal.withdrawalRequestId}</span>
                  </div>

                  <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Withdrawal</span>
                </div>
              </SearchResultItem>
            }

            const deposit = res.managedEvent.eventData.deposit
            if (deposit) {
              const crypto = deposit.depositMethod.Crypto
              const fiat = deposit.depositMethod.Fiat
              const method = crypto
                ? getMethod(crypto.network, crypto.token)
                : getMethod(fiat?.onrampNetwork || 'Fiat', fiat?.onrampToken || fiat?.fiatCurrency)
              const amount = crypto
                ? `${Number(deposit.grossAmount)} ${method.ticker}`
                : `${Number(deposit.fiatGrossAmount || deposit.grossAmount)} ${fiat?.fiatCurrency ?? method.ticker}`

              return <SearchResultItem key={i} to={searchResultLink(res)} onClick={() => setFocused(false)}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url(${method.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }} />
                      <span>Deposit for {amount}</span>
                    </div>

                    <span style={{ opacity: 0.4 }}>{deposit.depositId}</span>
                  </div>

                  <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Deposit</span>
                </div>
              </SearchResultItem>
            }

            const refund = res.managedEvent.eventData.refund
            if (refund) {
              const method = getMethod(refund.network, refund.token)

              return <SearchResultItem key={i} to={searchResultLink(res)} onClick={() => setFocused(false)}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 20,
                        height: 20,
                        backgroundImage: `url(${method.logo})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative'
                      }} />
                      <span>Refund for {Number(refund.withdrawalAmount)}</span>
                    </div>

                    <span style={{ opacity: 0.4 }}>{refund.withdrawalRequestId}</span>
                  </div>

                  <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>Refund</span>
                </div>
              </SearchResultItem>
            }

            const subscriptionEvent =
              res.managedEvent.eventData.newSubscription ||
              res.managedEvent.eventData.subscriptionCancelled ||
              res.managedEvent.eventData.subscriptionExpired
            if (subscriptionEvent) {
              const label = res.managedEvent.eventData.subscriptionCancelled
                ? 'Subscription cancelled'
                : res.managedEvent.eventData.subscriptionExpired
                  ? 'Subscription expired'
                  : 'Subscription'

              return <SearchResultItem key={i} to={searchResultLink(res)} onClick={() => setFocused(false)}>
                {
                  subscriptionEvent &&
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{label}</span>
                      </div>
                      <span style={{ opacity: 0.4 }}>{subscriptionEvent.subscriptionId}</span>

                    </div>
                    <span style={{ color: RADOM_COLORS.GRAY_DARKER }}>{label}</span>
                  </div>
                }

              </SearchResultItem>
            }

            return null
          })
      }
      {
        !isLoading && searchResults.length === 0 &&
        <SearchResultPlaceholder>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            height: 300
          }}>
            <span style={{ fontSize: 14 }}>No search results</span>
            <span style={{ opacity: 0.5 }}>Your search query returned no results</span>
          </div>
        </SearchResultPlaceholder>
      }
      {
        isLoading &&
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: 300
        }}>
          <Spinner />
        </div>
      }
    </div>
  </SearchContainer>
})

export default SearchBox
